import type { UploadState } from '~/hooks/use-upload-with-progress'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { Progress } from '~/components/ui/progress'
import { formatBytes, usagePercent } from '~/utils/bytes'

type Props = {
  state: UploadState
  /** Ce que le serveur fait une fois le fichier reçu (« Extraction et vérification… »). */
  processingLabel: string
  onCancel: () => void
}

/**
 * Progression d'un envoi en deux temps (#310) : l'envoi, mesuré et annulable,
 * puis le traitement serveur, de durée inconnue, en barre indéterminée.
 *
 * Chaque changement de phase est annoncé une fois (`aria-live`) ; le
 * pourcentage, lui, reste sur la barre (`aria-valuenow`), à la demande — le
 * dire à chaque pas couvrirait tout le reste. Au repos, seule la région
 * d'annonce est rendue : elle doit exister avant le premier envoi pour que
 * les lecteurs d'écran en lisent le début.
 */
function UploadProgress({ state, processingLabel, onCancel }: Props) {
  const { t, i18n } = useTranslation()

  const remaining = (seconds: number | null) => {
    if (seconds == null) return null
    return seconds < 60
      ? t('upload.remaining.seconds', { count: seconds })
      : t('upload.remaining.minutes', { count: Math.ceil(seconds / 60) })
  }

  const percent = state.phase === 'uploading' ? Math.floor(usagePercent(state.loaded, state.total)) : 0

  return (
    <div className="flex flex-col gap-2">
      <p className="sr-only" aria-live="polite">
        {state.phase === 'uploading' && t('upload.announce.uploading')}
        {state.phase === 'processing' && processingLabel}
      </p>

      {state.phase === 'uploading' && (
        <>
          <div className="flex items-center justify-between gap-2 text-sm">
            <span className="font-medium">{t('upload.sending', { percent })}</span>
            <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
              <X aria-hidden className="mr-1 size-4" />
              {t('common.cancel')}
            </Button>
          </div>
          <Progress value={percent} aria-label={t('upload.progress')} className="h-2" />
          {/* Pas de « 0 o sur 0 o » avant le premier relevé : le total n'est pas encore connu. */}
          {state.total > 0 && (
            <p className="text-xs text-muted-foreground tabular-nums">
              {[
                t('upload.bytes', {
                  loaded: formatBytes(state.loaded, i18n.language),
                  total: formatBytes(state.total, i18n.language),
                }),
                remaining(state.remainingSeconds),
              ].filter(Boolean).join(' · ')}
            </p>
          )}
        </>
      )}

      {state.phase === 'processing' && (
        <>
          <p className="text-sm font-medium">{processingLabel}</p>
          <div
            role="progressbar"
            aria-label={processingLabel}
            className="relative h-2 w-full overflow-hidden rounded-full bg-secondary"
          >
            <div className="animate-indeterminate absolute inset-y-0 w-1/3 rounded-full bg-primary-text" />
          </div>
        </>
      )}
    </div>
  )
}

export default UploadProgress
